import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { LinkButton, Owners, User } from 'components/common';
import {
  CalendarEventDateString,
  CalendarEventTilePicture,
  CalendarEventTileHeader
} from '.';

import type { CalendarEvent, UserWithBasicData } from 'shared-types';
import type { ButtonCategory, ButtonSize } from 'components/common/Button';

type CalendarEventAttributes = Pick<
  CalendarEvent,
  'id' | 'title' | 'description' | 'endDate' | 'imageUrl' | 'startDate'
>;

export type CalendarEventInAdminTile = CalendarEventAttributes & {
  owners: UserWithBasicData[];
  userIsEnrolled: boolean;
};

interface Props {
  event: CalendarEventInAdminTile;
}

const CalendarEventAdminTile = ({ event }: Props) => {
  const { messages, theme } = useReduxState();

  const router = useRouter();

  const handleClick = () => {
    router.push(`/calendar/${event.id}`);
  };

  const linkProps = {
    category: 'lighter' as ButtonCategory,
    isSemiRounded: true,
    onClick: (ev) => ev.stopPropagation(),
    size: 'small' as ButtonSize,
    style: {
      flexShrink: 0,
      fontSize: '1.3rem',
      width: 30,
      height: 30
    }
  };

  return (
    <div className="event-tile" onClick={handleClick}>
      <div className="picture">
        <CalendarEventTilePicture picture={event.imageUrl} size={480} />
      </div>

      <div className="content">
        <CalendarEventTileHeader event={event} />

        <div className="event-dates">
          <CalendarEventDateString
            startDate={event.startDate as string}
            endDate={event.endDate as string}
          />
        </div>

        <div className="owners">
          {event.owners?.length ? (
            <>
              <User
                showNameOrUsername={false}
                size="tiny"
                user={event.owners?.[0]}
              />

              <Owners owners={event.owners} />
            </>
          ) : (
            messages.commonNoOwners
          )}
        </div>

        <div className="footer">
          <div className="actions">
            <LinkButton
              href={`/calendar/${event.id}/settings`}
              icon="cog"
              tooltip={messages.commonSettings}
              {...linkProps}
            />
            <LinkButton
              href={`/calendar/${event.id}/participant-settings`}
              icon="user-cog"
              tooltip={messages.eventCalendarParticipantsSettings}
              {...linkProps}
            />
            <LinkButton
              href={`/calendar/${event.id}/access`}
              icon="eye"
              tooltip={messages.eventCalendarSettingsAccess}
              {...linkProps}
            />
            <LinkButton
              href={`/calendar/${event.id}/participants`}
              icon="users"
              tooltip={messages.commonParticipants}
              {...linkProps}
            />
            <LinkButton
              href={`/calendar/${event.id}/events`}
              icon="list"
              tooltip={messages.commonActivityLog}
              {...linkProps}
            />
            <LinkButton
              href={`/calendar/${event.id}/admin`}
              icon="tools"
              tooltip={messages.pagesManage}
              {...linkProps}
            />
          </div>
        </div>
      </div>

      <style jsx>{`
        .event-tile {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          box-shadow: ${theme.cardBoxShadow};
          cursor: pointer;
          display: flex;
          flex-direction: column;
          transition: box-shadow 0.3s;
          height: 100%;
        }
        .event-tile:focus,
        .event-tile:hover {
          box-shadow: ${theme.cardBoxShadowActive};
        }
        .picture {
          padding: 15px 15px 0;
        }
        .content {
          border-radius: ${theme.cardBorderRadius};
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          min-height: 180px;
          padding: 15px;
        }
        .event-dates {
          color: #555;
          font-size: 1.3rem;
          font-weight: 600;
          margin-top: 5px;
        }
        .owners {
          align-items: center;
          color: ${theme.textColorLight};
          display: inline-flex;
          flex-shrink: 0;
          font-size: 1.3rem;
          font-weight: 600;
          gap: 5px;
          margin-top: 10px;
        }
        .footer {
          display: flex;
          flex-direction: column;
          gap: 15px;
          margin-top: auto;
          padding-top: 20px;
        }
        .actions {
          display: flex;
          flex-grow: 0;
          flex-shrink: 0;
          flex-wrap: wrap;
          gap: 10px;
        }
      `}</style>
    </div>
  );
};

export default CalendarEventAdminTile;
